// Carry Roll Session slice — sessions, profiles, import (IndexedDB-backed).

import type { StateCreator } from 'zustand';
import { SessionType, ClubCategory, type BagEntry, type Session, type ShotProfile, type ShotRecord } from '@carry-roll/shared-types';
import { cryIDBAdapter } from '../persist/idb';
import { useStore } from './index';

const PERSIST_ERR = 'Storage error — could not save.';

/**
 * Fire-and-forget persist of a session to IndexedDB. Rejections (e.g. private
 * mode, AppLock still locked) never crash the UI — they surface via persistError.
 */
function persistSession(session: Session): void {
  cryIDBAdapter.putSession(session).catch(() => {
    useStore.setState({ persistError: PERSIST_ERR });
  });
}

export type MonitorFormat = 'garmin' | 'skytrak' | 'mevo' | 'foresight' | 'rapsodo';

export interface SessionSlice {
  sessions: Session[];
  profiles: ShotProfile[];
  bag: BagEntry[];
  activeSessionId: string | null;
  createSession: (name: string, type: SessionType, cat: ClubCategory) => string;
  setActiveSession: (sessionId: string) => void;
  deleteSession: (sessionId: string) => void;
  addShot: (sessionId: string, rec: ShotRecord) => void;
  toggleExcluded: (sessionId: string, shotId: string) => void;
  recomputeAggregates: (sessionId: string) => void;
  importCsv: (records: ShotRecord[], format: MonitorFormat) => void;
  saveProfile: (p: ShotProfile) => void;
  addBagEntry: (entry: BagEntry) => void;
}

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export const createSessionSlice: StateCreator<SessionSlice, [], [], SessionSlice> = (set, get) => ({
  sessions: [],
  profiles: [],
  bag: [],
  activeSessionId: null,
  createSession: (name, type, cat) => {
    const id = genId();
    const now = Date.now();
    const session: Session = {
      id,
      name,
      type,
      clubCategory: cat,
      shots: [],
      avgCarryM: null,
      avgTotalM: null,
      ballSpeedVariance: null,
      createdAt: now,
      updatedAt: now,
    };
    set((state) => ({ sessions: [...state.sessions, session], activeSessionId: id }));
    persistSession(session);
    return id;
  },
  setActiveSession: (sessionId) => set({ activeSessionId: sessionId }),
  deleteSession: (sessionId) =>
    set((state) => ({
      sessions: state.sessions.filter((s) => s.id !== sessionId),
      activeSessionId: state.activeSessionId === sessionId ? null : state.activeSessionId,
    })),
  addShot: (sessionId, rec) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId ? { ...s, shots: [...s.shots, rec], updatedAt: Date.now() } : s,
      ),
    }));
    get().recomputeAggregates(sessionId);
  },
  toggleExcluded: (sessionId, shotId) => {
    set((state) => ({
      sessions: state.sessions.map((s) =>
        s.id === sessionId
          ? {
              ...s,
              shots: s.shots.map((sh) => (sh.id === shotId ? { ...sh, excluded: !sh.excluded } : sh)),
            }
          : s,
      ),
    }));
    get().recomputeAggregates(sessionId);
  },
  recomputeAggregates: (sessionId) => {
    set((state) => ({
      sessions: state.sessions.map((s) => {
        if (s.id !== sessionId) return s;
        const included = s.shots.filter((sh) => !sh.excluded);
        if (included.length === 0) return { ...s, avgCarryM: null, avgTotalM: null, ballSpeedVariance: null };
        const avgCarryM = included.reduce((a, b) => a + b.carryM, 0) / included.length;
        const avgTotalM = included.reduce((a, b) => a + b.totalM, 0) / included.length;
        const ballSpeeds = included.map((sh) => sh.request.launch.ballSpeed);
        const meanBs = ballSpeeds.reduce((a, b) => a + b, 0) / ballSpeeds.length;
        const ballSpeedVariance =
          ballSpeeds.reduce((a, b) => a + (b - meanBs) ** 2, 0) / ballSpeeds.length;
        return { ...s, avgCarryM, avgTotalM, ballSpeedVariance, updatedAt: Date.now() };
      }),
    }));
    const updated = get().sessions.find((s) => s.id === sessionId);
    if (updated) persistSession(updated);
  },
  importCsv: (records, _format) => {
    const id = get().createSession(`Import ${new Date().toLocaleDateString()}`, SessionType.Indoor, ClubCategory.Driver);
    records.forEach((rec) => get().addShot(id, rec));
  },
  saveProfile: (p) => {
    set((state) => ({ profiles: [...state.profiles.filter((x) => x.id !== p.id), p] }));
    cryIDBAdapter.putProfile(p).catch(() => {
      useStore.setState({ persistError: PERSIST_ERR });
    });
  },
  addBagEntry: (entry) => {
    set((state) => ({ bag: [...state.bag.filter((b) => b.clubName !== entry.clubName), entry] }));
    cryIDBAdapter.putBagEntry(entry).catch(() => {
      useStore.setState({ persistError: PERSIST_ERR });
    });
  },
});
